import type * as THREE from "three";
import type { LevelStreamer } from "../level/LevelStreamer";
import type {
  PanelInstance,
  Segment,
  SegmentFactory,
} from "../level/SegmentFactory";
import type { WorldRotator } from "../world/WorldRotator";
import { shapeOf } from "../config/shapes";

// Anchor emissive intensity for the lit floor tile. The tunnel walls
// themselves sit at ~0.18 (see SegmentFactory.getMaterial) so this
// number controls how much the active tile pops above the baseline --
// the bloom pass only really lifts pixels above ~0.6 * exposure, so
// keep this comfortably above that.
const HIGHLIGHT_BASE = 1.1;
// How much the audio low-band lifts the active tile. Drives the
// kick-driven pulse that makes the tile feel alive on the beat.
const HIGHLIGHT_AUDIO_GAIN = 0.85;
// One-shot boost applied during gate transitions (GateTransition.gateBoost
// is 0..1 across the morph window).
const HIGHLIGHT_GATE_GAIN = 0.9;

// Replaces EmissivePulse. Instead of pulsing every wall material in
// lockstep (which made the entire cylinder glow on every beat), this
// system swaps the SINGLE floor tile under the player onto a separate,
// brighter "highlight" material per shape. Everyone else stays on the
// dim base material, so the player visually reads the tile they're on
// as the only lit surface in the tunnel.
//
// Implementation notes:
//   * The factory caches one base + one highlight material per shape.
//     We never allocate per-frame -- swap is just a `mesh.material =`
//     reference assignment (cheap, no shader recompile because both
//     materials have identical defines).
//   * When the player rotates, the floor tile changes -- we detect the
//     mesh swap and restore the previous panel's material before
//     applying the highlight to the new one, so we never leave a
//     "ghost lit" tile behind.
//   * When the segment hosting the highlighted tile recycles, we drop
//     the reference (via onSegmentCleared) so the post-restore on the
//     next swap is a no-op (the mesh has already been removed from the
//     scene by detach()).
export class TileHighlight {
  private factory: SegmentFactory;
  private streamer: LevelStreamer;
  private rotator: WorldRotator;

  private currentMesh: THREE.Mesh | null = null;
  private currentSegment: Segment | null = null;
  // Cached at swap-in time so we can restore the panel's true original
  // material on swap-out -- pulse and mirror trick segments use a
  // different material than the calm base, so we can't blindly restore
  // factory.getMaterialByShape().
  private currentOriginalMaterial: THREE.Material | THREE.Material[] | null =
    null;

  constructor(
    factory: SegmentFactory,
    streamer: LevelStreamer,
    rotator: WorldRotator
  ) {
    this.factory = factory;
    this.streamer = streamer;
    this.rotator = rotator;
    // If the segment hosting the lit tile is recycled, drop our
    // reference -- detach() has already torn the mesh out of the
    // worldGroup so trying to "restore" its material on the next swap
    // would just be writing to a dead Object3D.
    this.streamer.onSegmentCleared.on((seg) => {
      if (seg === this.currentSegment) {
        this.currentMesh = null;
        this.currentSegment = null;
        this.currentOriginalMaterial = null;
      }
    });
  }

  update(audioLow: number, gateBoost: number): void {
    const seg = this.streamer.getSegmentAtPlayer();
    if (!seg || seg.panels.length === 0) {
      this.releaseHighlight();
      return;
    }

    const sides = shapeOf(seg.shape).sides;
    const floorIdx = this.rotator.getFloorPanelIndex(sides);
    const panel = findPanel(seg.panels, floorIdx);
    if (!panel) {
      // Floor index resolves to a gap in this segment -- the ball is
      // currently airborne over the void. No tile to light.
      this.releaseHighlight();
      return;
    }

    if (panel.mesh !== this.currentMesh) {
      this.releaseHighlight();
      const hl = this.factory.getHighlightMaterialByShape(seg.shape);
      if (hl) {
        // Snapshot the panel's REAL material before the swap so
        // releaseHighlight can put it back. Pulse / mirror tricks
        // mean this isn't always the calm base.
        this.currentOriginalMaterial = panel.mesh.material;
        panel.mesh.material = hl;
        this.currentMesh = panel.mesh;
        this.currentSegment = seg;
      }
    }

    // Audio + gate modulation runs every frame on the highlight
    // material itself; the base material stays dim and untouched.
    const hl = this.factory.getHighlightMaterialByShape(seg.shape);
    if (hl) {
      hl.emissiveIntensity =
        HIGHLIGHT_BASE +
        audioLow * HIGHLIGHT_AUDIO_GAIN +
        gateBoost * HIGHLIGHT_GATE_GAIN;
    }
  }

  // Use on game-state transitions (menu, dead, level reset) to make
  // sure no panel is left on the highlight material when the ball is
  // no longer "on" anything in the scene.
  clear(): void {
    this.releaseHighlight();
  }

  private releaseHighlight(): void {
    if (!this.currentMesh) {
      this.currentSegment = null;
      this.currentOriginalMaterial = null;
      return;
    }
    if (this.currentOriginalMaterial) {
      this.currentMesh.material = this.currentOriginalMaterial;
    } else if (this.currentSegment) {
      const base = this.factory.getMaterialByShape(this.currentSegment.shape);
      if (base) this.currentMesh.material = base;
    }
    this.currentMesh = null;
    this.currentSegment = null;
    this.currentOriginalMaterial = null;
  }
}

function findPanel(
  panels: PanelInstance[],
  panelIndex: number
): PanelInstance | undefined {
  for (let i = 0; i < panels.length; i++) {
    if (panels[i]!.panelIndex === panelIndex) return panels[i];
  }
  return undefined;
}
